import { getMedia, getTranscriptMediaIds } from '@/infrastructure/storage'
import { mediaLibraryService } from '@/features/media-library/services/media-library-service'
import { useMediaLibraryStore } from '@/features/media-library/stores/media-library-store'
import { useTimelineStore } from '@/features/editor/deps/timeline-store'
import type { MediaMetadata } from '@/types/storage'
import { getCurrentProject, type AssistantProjectContextSnapshot } from './project-tools'

export interface LoadedAssistantMediaAsset {
  media: MediaMetadata
  blob: Blob
}

export interface AssistantProjectMediaItem extends MediaMetadata {
  projectId: string
}

export interface AssistantProjectMediaSnapshot {
  projectId: string
  total: number
  items: AssistantProjectMediaItem[]
}

export interface AssistantProjectMediaAnalysisItem {
  mediaId: string
  fileName: string
  mimeType: string
  kind: 'video' | 'audio' | 'image' | 'other'
  durationSeconds: number
  width: number
  height: number
  fps: number
  fileSize: number
  tags: string[]
  hasTranscript: boolean
  isUsedOnTimeline: boolean
  timelineItemCount: number
}

export interface AssistantProjectMediaAnalysis {
  project: AssistantProjectContextSnapshot
  media: {
    totalItems: number
    totalDurationSeconds: number
    totalFileSize: number
    transcriptReadyCount: number
    usedOnTimelineCount: number
    videoCount: number
    audioCount: number
    imageCount: number
    otherCount: number
  }
  timeline: {
    trackCount: number
    itemCount: number
    transitionCount: number
    totalDurationSeconds: number
    referencedMediaCount: number
  }
  items: AssistantProjectMediaAnalysisItem[]
}

function classifyMediaKind(mimeType: string): AssistantProjectMediaAnalysisItem['kind'] {
  if (mimeType.startsWith('video/')) {
    return 'video'
  }
  if (mimeType.startsWith('audio/')) {
    return 'audio'
  }
  if (mimeType.startsWith('image/')) {
    return 'image'
  }
  return 'other'
}

export async function listProjectMedia(
  projectId?: string,
  options?: { refresh?: boolean },
): Promise<AssistantProjectMediaSnapshot> {
  let mediaStore = useMediaLibraryStore.getState()
  const resolvedProjectId = projectId ?? mediaStore.currentProjectId
  if (!resolvedProjectId) {
    throw new Error('No project is available for media listing.')
  }

  if (mediaStore.currentProjectId !== resolvedProjectId) {
    mediaStore.setCurrentProject(resolvedProjectId)
    mediaStore = useMediaLibraryStore.getState()
  }

  if (options?.refresh || mediaStore.mediaItems.length === 0) {
    await useMediaLibraryStore.getState().loadMediaItems()
    mediaStore = useMediaLibraryStore.getState()
  }

  const items = mediaStore.mediaItems.map((item) => ({
    ...item,
    projectId: resolvedProjectId,
  }))

  return {
    projectId: resolvedProjectId,
    total: items.length,
    items,
  }
}

export async function analyzeProjectMedia(
  projectId?: string,
): Promise<AssistantProjectMediaAnalysis> {
  const project = getCurrentProject()
  const mediaSnapshot = await listProjectMedia(projectId ?? project.mediaProjectId, {
    refresh: false,
  })
  const timeline = useTimelineStore.getState()
  const timelineItemCountsByMediaId = new Map<string, number>()

  for (const item of timeline.items) {
    const mediaId = 'mediaId' in item && typeof item.mediaId === 'string' ? item.mediaId : ''
    if (!mediaId) {
      continue
    }

    timelineItemCountsByMediaId.set(mediaId, (timelineItemCountsByMediaId.get(mediaId) ?? 0) + 1)
  }

  const transcriptReadyIds = await getTranscriptMediaIds(mediaSnapshot.items.map((item) => item.id))
  const analysisItems = mediaSnapshot.items.map<AssistantProjectMediaAnalysisItem>((item) => {
    const kind = classifyMediaKind(item.mimeType)
    const timelineItemCount = timelineItemCountsByMediaId.get(item.id) ?? 0

    return {
      mediaId: item.id,
      fileName: item.fileName,
      mimeType: item.mimeType,
      kind,
      durationSeconds: item.duration,
      width: item.width,
      height: item.height,
      fps: item.fps,
      fileSize: item.fileSize,
      tags: item.tags ?? [],
      hasTranscript: transcriptReadyIds.has(item.id),
      isUsedOnTimeline: timelineItemCount > 0,
      timelineItemCount,
    }
  })

  return {
    project,
    media: {
      totalItems: analysisItems.length,
      totalDurationSeconds: analysisItems.reduce((sum, item) => sum + item.durationSeconds, 0),
      totalFileSize: analysisItems.reduce((sum, item) => sum + item.fileSize, 0),
      transcriptReadyCount: analysisItems.filter((item) => item.hasTranscript).length,
      usedOnTimelineCount: analysisItems.filter((item) => item.isUsedOnTimeline).length,
      videoCount: analysisItems.filter((item) => item.kind === 'video').length,
      audioCount: analysisItems.filter((item) => item.kind === 'audio').length,
      imageCount: analysisItems.filter((item) => item.kind === 'image').length,
      otherCount: analysisItems.filter((item) => item.kind === 'other').length,
    },
    timeline: {
      trackCount: timeline.tracks.length,
      itemCount: timeline.items.length,
      transitionCount: timeline.transitions.length,
      totalDurationSeconds:
        timeline.items.reduce(
          (maxFrame, item) => Math.max(maxFrame, item.from + item.durationInFrames),
          0,
        ) / Math.max(1, timeline.fps),
      referencedMediaCount: timelineItemCountsByMediaId.size,
    },
    items: analysisItems,
  }
}

export async function importLocalMedia(options?: {
  projectId?: string
  url?: string
}): Promise<MediaMetadata[]> {
  let mediaStore = useMediaLibraryStore.getState()
  const resolvedProjectId = options?.projectId ?? mediaStore.currentProjectId
  if (!resolvedProjectId) {
    throw new Error('No project is available for media import.')
  }

  if (mediaStore.currentProjectId !== resolvedProjectId) {
    mediaStore.setCurrentProject(resolvedProjectId)
    await useMediaLibraryStore.getState().loadMediaItems()
    mediaStore = useMediaLibraryStore.getState()
  }

  if (options?.url?.trim()) {
    const imported = await mediaLibraryService.importMediaFromUrl(options.url.trim(), resolvedProjectId)
    await useMediaLibraryStore.getState().loadMediaItems()
    mediaStore = useMediaLibraryStore.getState()
    const refreshedMedia =
      mediaStore.mediaItems.find((item) => item.id === imported.id) ?? imported
    return [refreshedMedia]
  }

  return useMediaLibraryStore.getState().importMedia()
}

export async function loadAssistantMediaAsset(mediaId: string): Promise<LoadedAssistantMediaAsset> {
  const media = await getMedia(mediaId)
  if (!media) {
    throw new Error(`Media not found: ${mediaId}`)
  }

  if (!media.mimeType.startsWith('video/') && !media.mimeType.startsWith('image/')) {
    throw new Error(`Media ${media.fileName} is not an image or video asset.`)
  }

  const blob = await mediaLibraryService.getMediaFile(mediaId)
  if (!blob) {
    throw new Error(`Unable to read media file: ${media.fileName}`)
  }

  return {
    media,
    blob,
  }
}

export async function saveGeneratedImageToProjectMedia(
  file: File,
  projectId: string,
  options?: {
    tags?: string[]
    width?: number
    height?: number
    codec?: string
  },
): Promise<MediaMetadata> {
  return mediaLibraryService.importGeneratedImage(file, projectId, {
    tags: options?.tags,
    width: options?.width,
    height: options?.height,
    codec: options?.codec,
  })
}
